import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Search, Filter, Eye, Trash2, Calendar, User, CreditCard } from 'lucide-react';
import { fetchOrders, deleteOrderById, updateOrderById } from '../../redux/slices/orderSlice';
import { getMonthKey } from '../../services/orderService';
import toast from 'react-hot-toast';

const OrdersList = () => {
  const dispatch = useDispatch();
  const { orders, loading } = useSelector((state) => state.order);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);

  // Month filter state
  const generateMonthOptions = () => {
    const options = [];
    const d = new Date();
    for (let i = 0; i < 12; i++) {
      options.push(d.toLocaleString('en-US', { month: 'short' }) + '-' + d.getFullYear());
      d.setMonth(d.getMonth() - 1);
    }
    return options;
  };
  const [monthOptions] = useState(generateMonthOptions());
  const [selectedMonth, setSelectedMonth] = useState(getMonthKey());

  useEffect(() => {
    dispatch(fetchOrders(selectedMonth));
  }, [dispatch, selectedMonth]);

  // Filter orders
  const filteredOrders = orders.filter((order) => {
    const matchesSearch =
      order.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.userMobile?.includes(searchTerm) ||
      order.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.id?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    const matchesPayment = paymentFilter === 'all' || order.paymentMethod === paymentFilter;

    let matchesDate = true;
    if (dateFrom) {
      const orderDate = new Date(order.orderDate || order.createdAt);
      const fromDate = new Date(dateFrom);
      matchesDate = matchesDate && orderDate >= fromDate;
    }
    if (dateTo) {
      const orderDate = new Date(order.orderDate || order.createdAt);
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      matchesDate = matchesDate && orderDate <= toDate;
    }

    return matchesSearch && matchesStatus && matchesPayment && matchesDate;
  });

  const handleDelete = async (order) => {
    if (window.confirm('Are you sure you want to delete this order?')) {
      try {
        await dispatch(deleteOrderById({ orderId: order.id, monthKey: order.monthKey, dayKey: order.dayKey })).unwrap();
        toast.success('Order deleted successfully');
      } catch (error) {
        toast.error('Failed to delete order');
        console.error('Error:', error);
      }
    }
  };

  const handleStatusChange = async (order, newStatus) => {
    try {
      await dispatch(updateOrderById({ orderId: order.id, monthKey: order.monthKey, dayKey: order.dayKey, orderData: { status: newStatus } })).unwrap();
      toast.success('Order status updated');
    } catch (error) {
      toast.error('Failed to update order status');
      console.error('Error:', error);
    }
  };

  const viewOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowOrderDetails(true);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setPaymentFilter('all');
    setDateFrom('');
    setDateTo('');
    setSelectedMonth(getMonthKey());
  };

  return (
    <div className='max-w-full mx-auto p-4 md:p-6'>
      {/* Header */}
      <div className='mb-6'>
        <h1 className='text-2xl md:text-3xl font-bold text-gray-900 mb-2'>Orders Management</h1>
        <p className='text-gray-600'>View and manage all customer orders</p>
      </div>

      {/* Filters Bar */}
      <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6'>
        <div className='flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4'>
          {/* Search */}
          <div className='relative w-full md:w-96'>
            <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5' />
            <input
              type='text'
              placeholder='Search by customer, email, mobile, or order ID...'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className='w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all'
            />
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className='flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all'
          >
            <Filter className='w-5 h-5' />
            <span>{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
          </button>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className='grid grid-cols-1 md:grid-cols-5 gap-4 pt-4 border-t border-gray-200'>
            <div>
              <label className='block text-sm font-semibold text-gray-700 mb-2'>Billing Month</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white'
              >
                {monthOptions.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className='block text-sm font-semibold text-gray-700 mb-2'>Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white'
              >
                <option value='all'>All Status</option>
                <option value='pending'>Pending</option>
                <option value='processing'>Processing</option>
                <option value='completed'>Completed</option>
                <option value='cancelled'>Cancelled</option>
              </select>
            </div>

            <div>
              <label className='block text-sm font-semibold text-gray-700 mb-2'>
                Payment Method
              </label>
              <select
                value={paymentFilter}
                onChange={(e) => setPaymentFilter(e.target.value)}
                className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white'
              >
                <option value='all'>All Methods</option>
                <option value='cash'>Cash</option>
                <option value='upi'>UPI</option>
                <option value='card'>Card</option>
                <option value='online'>Online Payment</option>
              </select>
            </div>

            <div>
              <label className='block text-sm font-semibold text-gray-700 mb-2'>Date From</label>
              <input
                type='date'
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all'
              />
            </div>

            <div>
              <label className='block text-sm font-semibold text-gray-700 mb-2'>Date To</label>
              <input
                type='date'
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all'
              />
            </div>

            <div className='md:col-span-5 flex justify-end'>
              <button
                onClick={clearFilters}
                className='px-4 py-2 text-sm text-blue-600 hover:text-blue-700 font-medium'
              >
                Clear All Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className='grid grid-cols-1 md:grid-cols-4 gap-4 mb-6'>
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'>
          <div className='text-sm text-gray-600 mb-1'>Total Orders</div>
          <div className='text-2xl font-bold text-gray-900'>{filteredOrders.length}</div>
        </div>
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'>
          <div className='text-sm text-gray-600 mb-1'>Pending</div>
          <div className='text-2xl font-bold text-yellow-600'>
            {filteredOrders.filter((o) => o.status === 'pending').length}
          </div>
        </div>
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'>
          <div className='text-sm text-gray-600 mb-1'>Completed</div>
          <div className='text-2xl font-bold text-green-600'>
            {filteredOrders.filter((o) => o.status === 'completed').length}
          </div>
        </div>
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'>
          <div className='text-sm text-gray-600 mb-1'>Total Revenue</div>
          <div className='text-2xl font-bold text-blue-600'>
            ₹
            {filteredOrders
              .filter((o) => o.status === 'completed')
              .reduce((sum, order) => sum + parseFloat(order.total || 0), 0)
              .toFixed(2)}
          </div>
        </div>
      </div>

      {/* Desktop Table */}
      <div className='hidden lg:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'>
        <div className='overflow-x-auto'>
          <table className='w-full'>
            <thead className='bg-gray-50 border-b border-gray-200'>
              <tr>
                <th className='px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                  Order ID
                </th>
                <th className='px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                  Customer
                </th>
                <th className='px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                  Date
                </th>
                <th className='px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                  Items
                </th>
                <th className='px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                  Total
                </th>
                <th className='px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                  Payment
                </th>
                <th className='px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                  Status
                </th>
                <th className='px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-200'>
              {loading ? (
                <tr>
                  <td colSpan='8' className='px-6 py-12 text-center'>
                    <div className='flex justify-center items-center'>
                      <div className='w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin' />
                    </div>
                  </td>
                </tr>
              ) : filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan='8' className='px-6 py-12 text-center text-gray-500'>
                    No orders found
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => (
                  <tr key={order.id} className='hover:bg-gray-50 transition-colors'>
                    <td className='px-4 py-4 whitespace-nowrap'>
                      <div className='text-sm font-medium text-gray-900'>
                        #{order.id.substring(0, 8)}
                      </div>
                    </td>
                    <td className='px-4 py-4'>
                      <div className='text-sm font-medium text-gray-900'>{order.userName}</div>
                      <div className='text-xs text-gray-500'>{order.userMobile}</div>
                    </td>
                    <td className='px-4 py-4 whitespace-nowrap'>
                      <div className='text-sm text-gray-900'>
                        {new Date(order.orderDate || order.createdAt).toLocaleDateString()}
                      </div>
                      <div className='text-xs text-gray-500'>
                        {new Date(order.orderDate || order.createdAt).toLocaleTimeString()}
                      </div>
                    </td>
                    <td className='px-4 py-4 whitespace-nowrap'>
                      <div className='text-sm text-gray-900'>{order.items?.length || 0} items</div>
                    </td>
                    <td className='px-4 py-4 whitespace-nowrap'>
                      <div className='text-sm font-bold text-green-600'>₹{order.total}</div>
                      <div className='text-xs text-gray-500'>GST: ₹{order.gst}</div>
                    </td>
                    <td className='px-4 py-4 whitespace-nowrap'>
                      <div className='text-sm text-gray-900 capitalize'>{order.paymentMethod}</div>
                    </td>
                    <td className='px-4 py-4 whitespace-nowrap'>
                      <select
                        value={order.status}
                        onChange={(e) => handleStatusChange(order, e.target.value)}
                        className={`px-3 py-1 text-xs font-semibold rounded-full outline-none cursor-pointer ${getStatusBadgeClass(
                          order.status,
                        )}`}
                      >
                        <option value='pending'>Pending</option>
                        <option value='processing'>Processing</option>
                        <option value='completed'>Completed</option>
                        <option value='cancelled'>Cancelled</option>
                      </select>
                    </td>
                    <td className='px-4 py-4 whitespace-nowrap text-sm'>
                      <div className='flex gap-2'>
                        <button
                          onClick={() => viewOrderDetails(order)}
                          className='p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors'
                          title='View Details'
                        >
                          <Eye className='w-4 h-4' />
                        </button>
                        <button
                          onClick={() => handleDelete(order)}
                          className='p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors'
                          title='Delete'
                        >
                          <Trash2 className='w-4 h-4' />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Cards */}
      <div className='lg:hidden space-y-4'>
        {loading ? (
          <div className='flex justify-center items-center py-12'>
            <div className='w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin' />
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500'>
            No orders found
          </div>
        ) : (
          filteredOrders.map((order) => (
            <div
              key={order.id}
              className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'
            >
              <div className='flex justify-between items-start mb-3'>
                <div>
                  <h3 className='text-lg font-semibold text-gray-900'>{order.userName}</h3>
                  <p className='text-sm text-gray-500'>#{order.id.substring(0, 8)}</p>
                </div>
                <span
                  className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(
                    order.status,
                  )}`}
                >
                  {order.status}
                </span>
              </div>

              <div className='space-y-2 mb-4'>
                <div className='flex justify-between text-sm'>
                  <span className='text-gray-600'>Date:</span>
                  <span className='font-medium'>
                    {new Date(order.orderDate || order.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <div className='flex justify-between text-sm'>
                  <span className='text-gray-600'>Items:</span>
                  <span className='font-medium'>{order.items?.length || 0}</span>
                </div>
                <div className='flex justify-between text-sm'>
                  <span className='text-gray-600'>Payment:</span>
                  <span className='font-medium capitalize'>{order.paymentMethod}</span>
                </div>
                <div className='flex justify-between text-sm'>
                  <span className='text-gray-600'>Total:</span>
                  <span className='font-bold text-green-600'>₹{order.total}</span>
                </div>
              </div>

              <div className='flex gap-2 pt-3 border-t border-gray-200'>
                <button
                  onClick={() => viewOrderDetails(order)}
                  className='flex items-center gap-2 px-4 py-2 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors flex-1 justify-center'
                >
                  <Eye className='w-4 h-4' />
                  <span>View</span>
                </button>
                <select
                  value={order.status}
                  onChange={(e) => handleStatusChange(order, e.target.value)}
                  className='px-4 py-2 border border-gray-300 rounded-lg text-sm flex-1'
                >
                  <option value='pending'>Pending</option>
                  <option value='processing'>Processing</option>
                  <option value='completed'>Completed</option>
                  <option value='cancelled'>Cancelled</option>
                </select>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Order Details Modal */}
      {showOrderDetails && selectedOrder && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto'>
            <div className='sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center'>
              <h2 className='text-xl font-semibold text-gray-900'>Order Details</h2>
              <button
                onClick={() => setShowOrderDetails(false)}
                className='text-gray-400 hover:text-gray-600'
              >
                ✕
              </button>
            </div>

            <div className='p-6 space-y-6'>
              {/* Order Info */}
              <div>
                <h3 className='text-lg font-semibold text-gray-900 mb-3'>Order Information</h3>
                <div className='grid grid-cols-2 gap-3 text-sm'>
                  <div>
                    <span className='text-gray-600'>Order ID:</span>
                    <span className='font-semibold ml-2'>#{selectedOrder.id}</span>
                  </div>
                  <div>
                    <span className='text-gray-600'>Date:</span>
                    <span className='font-semibold ml-2'>
                      {new Date(
                        selectedOrder.orderDate || selectedOrder.createdAt,
                      ).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className='text-gray-600'>Status:</span>
                    <span
                      className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(
                        selectedOrder.status,
                      )}`}
                    >
                      {selectedOrder.status}
                    </span>
                  </div>
                  <div>
                    <span className='text-gray-600'>Payment:</span>
                    <span className='font-semibold ml-2 capitalize'>
                      {selectedOrder.paymentMethod}
                    </span>
                  </div>
                </div>
              </div>

              {/* Customer Info */}
              <div>
                <h3 className='text-lg font-semibold text-gray-900 mb-3'>Customer Information</h3>
                <div className='grid grid-cols-1 gap-2 text-sm'>
                  <div>
                    <span className='text-gray-600'>Name:</span>
                    <span className='font-semibold ml-2'>{selectedOrder.userName}</span>
                  </div>
                  <div>
                    <span className='text-gray-600'>Mobile:</span>
                    <span className='font-semibold ml-2'>{selectedOrder.userMobile}</span>
                  </div>
                  <div>
                    <span className='text-gray-600'>Email:</span>
                    <span className='font-semibold ml-2'>{selectedOrder.userEmail}</span>
                  </div>
                </div>
              </div>

              {/* Items */}
              <div>
                <h3 className='text-lg font-semibold text-gray-900 mb-3'>Order Items</h3>
                <div className='space-y-3'>
                  {selectedOrder.items?.map((item, index) => (
                    <div
                      key={index}
                      className='flex justify-between items-center p-3 bg-gray-50 rounded-lg'
                    >
                      <div className='flex-1'>
                        <div className='font-semibold text-gray-900'>{item.productName}</div>
                        <div className='text-xs text-gray-500'>{item.productCode}</div>
                        <div className='text-sm text-gray-600 mt-1'>
                          ₹{item.price} × {item.quantity}
                        </div>
                      </div>
                      <div className='text-right'>
                        <div className='font-bold text-gray-900'>
                          ₹{(item.price * item.quantity).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Summary */}
              <div className='border-t border-gray-200 pt-4'>
                <div className='space-y-2 text-sm'>
                  <div className='flex justify-between'>
                    <span className='text-gray-600'>Subtotal:</span>
                    <span className='font-semibold'>₹{selectedOrder.subtotal}</span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-gray-600'>GST (18%):</span>
                    <span className='font-semibold'>₹{selectedOrder.gst}</span>
                  </div>
                  <div className='flex justify-between text-lg font-bold border-t border-gray-200 pt-2'>
                    <span>Total:</span>
                    <span className='text-green-600'>₹{selectedOrder.total}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersList;
